/**
 * AcquireOS Workflow Notification Service
 * Sends email alerts for urgent/overdue workflow events via EmailJS Template 2.
 *
 * Template 1  (VITE_EMAILJS_TEMPLATE_ID)       — email verification (already wired)
 * Template 2  (VITE_EMAILJS_ALERT_TEMPLATE_ID) — workflow alerts    (this service)
 *
 * When VITE_EMAILJS_ALERT_TEMPLATE_ID is blank the service falls back to
 * console.info so the UI still works in dev / demo mode.
 */

import emailjs from '@emailjs/browser';

// ── Account 1 — verification + general workflow alerts ────────────────────────
const EMAILJS_SERVICE_ID        = import.meta.env.VITE_EMAILJS_SERVICE_ID        || '';
const EMAILJS_PUBLIC_KEY        = import.meta.env.VITE_EMAILJS_PUBLIC_KEY        || '';
const EMAILJS_ALERT_TEMPLATE_ID = import.meta.env.VITE_EMAILJS_ALERT_TEMPLATE_ID
                                 || import.meta.env.VITE_EMAILJS_WORKFLOW_TEMPLATE_ID
                                 || '';

// ── Account 2 — pipeline stall + missing OM nudges ────────────────────────────
// Falls back to Account 1 credentials if Account 2 is not configured yet.
const EMAILJS_SERVICE_ID_2      = import.meta.env.VITE_EMAILJS_SERVICE_ID_2      || EMAILJS_SERVICE_ID;
const EMAILJS_PUBLIC_KEY_2      = import.meta.env.VITE_EMAILJS_PUBLIC_KEY_2      || EMAILJS_PUBLIC_KEY;

// Template 3 — "Resume Your Deal Analysis Pipeline"   {{to_name}}, {{deal_name}}
const EMAILJS_STALL_TEMPLATE_ID = import.meta.env.VITE_EMAILJS_STALL_TEMPLATE_ID || EMAILJS_ALERT_TEMPLATE_ID;

// Template 4 — "Mandatory Action Required: Upload Documents"   {{to_name}}, {{deal_name}}, {{mandatory_items}}
const EMAILJS_DOCS_TEMPLATE_ID  = import.meta.env.VITE_EMAILJS_DOCS_TEMPLATE_ID  || EMAILJS_ALERT_TEMPLATE_ID;

// ── Account 3 — idle dashboard nudge (no deal selected for 4h) ───────────────
// Falls back to Account 1 credentials if Account 3 is not configured yet.
const EMAILJS_SERVICE_ID_3      = import.meta.env.VITE_EMAILJS_SERVICE_ID_3      || EMAILJS_SERVICE_ID;
const EMAILJS_PUBLIC_KEY_3      = import.meta.env.VITE_EMAILJS_PUBLIC_KEY_3      || EMAILJS_PUBLIC_KEY;

// Template 5 — idle dashboard nudge
const EMAILJS_IDLE_TEMPLATE_ID  = import.meta.env.VITE_EMAILJS_IDLE_TEMPLATE_ID  || EMAILJS_ALERT_TEMPLATE_ID;

const DEV_MODE  = !EMAILJS_SERVICE_ID  || !EMAILJS_ALERT_TEMPLATE_ID || !EMAILJS_PUBLIC_KEY;
const DEV_MODE2 = !EMAILJS_SERVICE_ID_2 || !EMAILJS_PUBLIC_KEY_2;
const DEV_MODE3 = !EMAILJS_SERVICE_ID_3 || !EMAILJS_PUBLIC_KEY_3;

// ── Rate-limiting: don't spam the same alert within 1 hour ───────────────────
const _sentAlerts = new Map(); // alertId → timestamp
const COOLDOWN_MS = 60 * 60 * 1000; // 1 hour

function shouldSend(alertId) {
  const last = _sentAlerts.get(alertId);
  if (!last) return true;
  return Date.now() - last > COOLDOWN_MS;
}

function markSent(alertId) {
  _sentAlerts.set(alertId, Date.now());
}

// ── Format payload — variable names MUST match the EmailJS alert template ─────
//
//  Template variables used:
//    {{to_email}}       {{to_name}}        {{subject}}
//    {{deal_name}}      {{module_number}}  {{alert_title}}
//    {{alert_message}}  {{alert_severity}} {{alert_action}}
//    {{sent_at}}        {{company_name}}   {{support_email}}
//
function formatAlertEmail(alert, dealName, userEmail) {
  const severityLabel = alert.severity.toUpperCase();
  return {
    to_email:       userEmail,
    to_name:        'AcquireOS User',
    subject:        `[${severityLabel}] AcquireOS: ${alert.title}`,
    deal_name:      dealName || 'Your current deal',
    module_number:  alert.moduleNumber > 0 ? String(alert.moduleNumber) : 'General',
    alert_title:    alert.title,
    alert_message:  alert.message,
    alert_severity: severityLabel,
    alert_action:   alert.actionText || 'Open Action Center',
    sent_at:        new Date().toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }),
    company_name:   'AcquireOS AI',
    support_email:  'info@acquireosai.com',
    // Ensure email sends FROM AcquireOS AI address, not the connected Gmail account
    from_name:      'AcquireOS AI',
    from_email:     'info@acquireosai.com',
    sender_name:    'AcquireOS AI Team',
    reply_to:       'info@acquireosai.com',
  };
}

// ── Mock sender for dev/demo mode ─────────────────────────────────────────────
function sendMock(alert, dealName, userEmail) {
  console.info(
    `%c[AcquireOS Workflow Email — DEV MODE]`,
    'color:#1d4ed8;font-weight:bold',
    `\nTo: ${userEmail}`,
    `\nDeal: ${dealName}`,
    `\n[${alert.severity.toUpperCase()}] Module ${alert.moduleNumber}: ${alert.title}`,
    `\n${alert.message}`,
    `\nAction: ${alert.actionText}`,
  );
  return { success: true, mode: 'dev' };
}

// ── Main send function ────────────────────────────────────────────────────────
export async function sendWorkflowAlertEmail(alert, dealName = '', userEmail = '') {
  if (!userEmail) return { success: false, error: 'No user email provided' };

  // Only email urgent and overdue alerts
  if (!['urgent', 'overdue'].includes(alert.severity)) {
    return { success: false, skipped: true, reason: 'severity below email threshold' };
  }

  // Rate limit — skip if the same alert was sent within the last hour
  if (!shouldSend(alert.id)) {
    return { success: false, skipped: true, reason: 'cooldown active' };
  }

  if (DEV_MODE) {
    markSent(alert.id);
    return sendMock(alert, dealName, userEmail);
  }

  try {
    emailjs.init({ publicKey: EMAILJS_PUBLIC_KEY });
    await emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_ALERT_TEMPLATE_ID,
      formatAlertEmail(alert, dealName, userEmail),
    );
    markSent(alert.id);
    return { success: true };
  } catch (err) {
    console.error('[WorkflowNotification] Email send failed:', err);
    return { success: false, error: err?.text || err?.message || 'Send failed' };
  }
}

// ── Batch: send all urgent/overdue alerts that haven't been sent recently ─────
export async function sendPendingAlertEmails(alerts = [], dealName = '', userEmail = '') {
  if (!userEmail || !alerts.length) return;
  const toSend = alerts.filter(a =>
    ['urgent', 'overdue'].includes(a.severity) && shouldSend(a.id)
  );
  // Stagger sends to avoid rate limits (max 5 per batch)
  for (const alert of toSend.slice(0, 5)) {
    await sendWorkflowAlertEmail(alert, dealName, userEmail);
    await new Promise(r => setTimeout(r, 400));
  }
}

// ── Digest: summarize all pending alerts in one email ─────────────────────────
export async function sendAlertDigestEmail(alerts = [], dealName = '', userEmail = '') {
  if (!userEmail || !alerts.length) return { success: false };

  const urgent  = alerts.filter(a => a.severity === 'urgent').length;
  const overdue = alerts.filter(a => a.severity === 'overdue').length;
  const warning = alerts.filter(a => a.severity === 'warning').length;
  const total   = alerts.length;

  const summaryLines = alerts
    .filter(a => ['overdue', 'urgent', 'warning'].includes(a.severity))
    .slice(0, 8)
    .map(a => `• [${a.severity.toUpperCase()}] Module ${a.moduleNumber}: ${a.title}`)
    .join('\n');

  // Build a synthetic "alert" object so formatAlertEmail can shape the payload
  const digestAlert = {
    id:           'digest_' + Date.now(),
    severity:     overdue > 0 ? 'overdue' : urgent > 0 ? 'urgent' : 'warning',
    moduleNumber: 0,
    title:        `Workflow Digest — ${total} pending alert${total !== 1 ? 's' : ''}`,
    message:      `You have ${overdue} overdue, ${urgent} urgent, and ${warning} warning items across your deal pipeline.\n\n${summaryLines}`,
    actionText:   'Open Action Center',
    category:     'digest',
  };

  if (DEV_MODE) return sendMock(digestAlert, dealName, userEmail);

  try {
    emailjs.init({ publicKey: EMAILJS_PUBLIC_KEY });
    await emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_ALERT_TEMPLATE_ID,
      formatAlertEmail(digestAlert, dealName, userEmail),
    );
    return { success: true };
  } catch (err) {
    console.error('[WorkflowNotification] Digest send failed:', err);
    return { success: false, error: err?.text || err?.message };
  }
}

// ╔══════════════════════════════════════════════════════════════════════════════╗
// ║  IDLE / NUDGE EMAILS  (sent at most once per 24 hours via idleNotificationService)
// ╚══════════════════════════════════════════════════════════════════════════════╝

// Low-level sender — account 1, 2, or 3 selected by `account` param (default: 1)
async function sendTemplateEmail(templateId, payload, label = 'Nudge', account = 1) {
  const serviceId = account === 3 ? EMAILJS_SERVICE_ID_3
                  : account === 2 ? EMAILJS_SERVICE_ID_2
                  :                 EMAILJS_SERVICE_ID;
  const publicKey = account === 3 ? EMAILJS_PUBLIC_KEY_3
                  : account === 2 ? EMAILJS_PUBLIC_KEY_2
                  :                 EMAILJS_PUBLIC_KEY;
  const isDevMode = account === 3 ? DEV_MODE3
                  : account === 2 ? DEV_MODE2
                  :                 DEV_MODE;

  if (isDevMode) {
    console.info(
      `%c[AcquireOS ${label} — DEV MODE (account ${account})]`,
      'color:#2563eb;font-weight:bold',
      '\nPayload:', payload,
    );
    return { success: true, mode: 'dev' };
  }
  try {
    emailjs.init({ publicKey });
    await emailjs.send(serviceId, templateId, payload);
    return { success: true };
  } catch (err) {
    console.error(`[WorkflowNotification] ${label} send failed:`, err);
    return { success: false, error: err?.text || err?.message };
  }
}

/**
 * Scenario 1 — User logged in but hasn't selected any deal for 4+ hours.
 * Uses Account 3 + Template 5 (dedicated idle dashboard nudge template).
 * Falls back to Account 1 general alert if Account 3 not yet configured.
 *
 * Add your template variables below to match whatever your EmailJS template expects.
 */
export async function sendIdleDashboardEmail(userEmail) {
  if (!userEmail) return { success: false };

  const payload = {
    to_email:       userEmail,
    to_name:        'AcquireOS User',
    deal_name:      'your deal pipeline',
    alert_title:    'Your AcquireOS Dashboard is Waiting for You',
    alert_message:  'You have been away for a while. Your deal analysis pipeline is paused and ready to continue. Log back in to pick up right where you left off.',
    alert_action:   'Return to AcquireOS Dashboard',
    alert_severity: 'info',
    module_number:  '—',
    stage_name:     '—',
    company_name:   'AcquireOS AI',
    support_email:  'info@acquireosai.com',
    sent_at:        new Date().toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }),
    from_name:      'AcquireOS AI',
    from_email:     'info@acquireosai.com',
    sender_name:    'AcquireOS AI Team',
    reply_to:       'info@acquireosai.com',
    subject:        'Your AcquireOS Dashboard is Waiting — Come Back & Continue',
  };

  return sendTemplateEmail(
    EMAILJS_IDLE_TEMPLATE_ID,
    payload,
    'Idle Dashboard Nudge',
    3,
  );
}

/**
 * Scenario 2 — User selected a deal but no module progress for 4+ hours.
 *
 * Uses Template 3: "Resume Your Deal Analysis Pipeline"
 * Template variables: {{to_name}}, {{deal_name}}
 */
export async function sendModuleStallEmail(userEmail, dealState = {}) {
  if (!userEmail) return { success: false };

  const dealName    = dealState.dealName || 'your current deal';
  const stageNumber = Number(dealState.pipelineStage) || 1;
  const stageNames  = {
    1: 'Market Intelligence', 2: 'Market Intelligence', 3: 'Deal Intake',
    4: 'Underwriting',        5: 'Deal Scoring',        6: 'Lender Match',
    7: 'Loan Package',        8: 'Lender Outreach',     9: 'Action Plan',
    10: 'LOI Drafting',       11: 'Contract Review',    12: 'Due Diligence',
    13: 'Price Negotiation',  14: 'Closing Costs',      15: 'Asset Management',
    16: 'Exit Strategy',
  };
  const stageName = stageNames[stageNumber] || `Module ${stageNumber}`;

  // Payload matches "Resume Your Deal Analysis Pipeline" template exactly
  const payload = {
    to_email:      userEmail,
    to_name:       'AcquireOS User',
    deal_name:     dealName,
    module_number: String(stageNumber),
    stage_name:    stageName,
    alert_title:   `Resume Your Deal Analysis — Module ${stageNumber}: ${stageName}`,
    alert_message: `Your underwriting process for "${dealName}" has been paused at Module ${stageNumber} (${stageName}). Pick up right where you left off to keep your deal moving forward.`,
    alert_action:  `Resume Module ${stageNumber} — ${stageName}`,
    alert_severity: 'info',
    company_name:  'AcquireOS AI',
    support_email: 'info@acquireosai.com',
    sent_at:       new Date().toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }),
    from_name:     'AcquireOS AI',
    from_email:    'info@acquireosai.com',
    sender_name:   'AcquireOS AI Team',
    reply_to:      'info@acquireosai.com',
    subject:       `Action Required: Resume Module ${stageNumber} — ${dealName}`,
  };

  return sendTemplateEmail(EMAILJS_STALL_TEMPLATE_ID, payload, 'Module Stall Nudge', 2);
}

/**
 * Scenario 3 — OM not uploaded for 4+ hours.
 *
 * Uses Template 4: "Mandatory Action Required: Upload Documents"
 * Template variables: {{to_name}}, {{deal_name}}, {{mandatory_items}}
 */
export async function sendMissingOMEmail(userEmail, dealState = {}) {
  if (!userEmail) return { success: false };

  const dealName = dealState.dealName || 'your current deal';

  // Payload matches "Mandatory Action Required: Upload Documents" template exactly
  const payload = {
    to_email:        userEmail,
    to_name:         'AcquireOS User',
    deal_name:       dealName,
    mandatory_items: '• Offering Memorandum (OM) — PDF required for financial extraction and underwriting',
    alert_title:     'Mandatory Action Required: Upload Your Documents',
    alert_message:   `Your deal "${dealName}" is missing required documents. The Offering Memorandum (OM) PDF must be uploaded in Module 3 before underwriting and financial extraction can proceed.`,
    alert_action:    'Upload OM in Module 3 — Deal Intake',
    alert_severity:  'urgent',
    module_number:   '3',
    stage_name:      'Deal Intake',
    company_name:    'AcquireOS AI',
    support_email:   'info@acquireosai.com',
    sent_at:         new Date().toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }),
    from_name:       'AcquireOS AI',
    from_email:      'info@acquireosai.com',
    sender_name:     'AcquireOS AI Team',
    reply_to:        'info@acquireosai.com',
    subject:         `Mandatory Action Required: Upload OM for ${dealName}`,
  };

  return sendTemplateEmail(EMAILJS_DOCS_TEMPLATE_ID, payload, 'Missing OM Nudge', 2);
}
